import { Button } from '@design/components/button/Button';
import { Icon } from '@design/components/icon/Icon';
import { config } from '@terminal/config';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { WatchlistError } from '@terminal/stores/use-watchlist/watchlist-types';
import { AlertMessage } from '@terminal/views/onboarding/alert-message/AlertMessage';
import { useAlertMessage } from '@terminal/views/onboarding/hooks/useAlertMessage';
import { ButtonLink } from '@toggle/design-system/src/components/button-link/ButtonLink';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatWatchlistManager } from '../chat-watchlist-manager-widget/ChatWatchlistManagerWidget';
import { TableWatchlistManagerWidget } from '../table-watchlist-manager-widget/TableWatchlistManagerWidget';
import { AssetFilter } from '../watchlist-manager-widget/components/asset-filter/AssetFilter';
import { useEntityChangeHandlers } from '../watchlist-manager-widget/useEntityChangeHandlers';
import { useEntityResolution } from '../watchlist-manager-widget/useEntityResolution';
import { useWatchlistForm } from '../watchlist-manager-widget/useWatchlistForm';
import { useWatchlistManagerMode } from '../watchlist-manager-widget/useWatchlistManagerMode';
import { useWatchlistManagerSave } from '../watchlist-manager-widget/useWatchlistManagerSave';
import styles from './EditWatchlistWidget.module.scss';
import { useWatchlistFilterSync } from './hooks/useWatchlistFilterSync';
import { buildRowSelectionFromTags } from './utils';

export interface EditWatchlistWidgetProps {
  onListCreated: (id: string) => void;
  onCancel: () => void;
}

export const EditWatchlistWidget = ({
  onListCreated,
  onCancel,
}: EditWatchlistWidgetProps) => {
  const { t } = useTranslation(['watchlistManager', 'common', 'widget']);

  const unMappedRowRef = useRef<HTMLTableRowElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const didEditPrefillRef = useRef(false);
  const [prefillTags, setPrefillTags] = useState<string[]>([]);

  const [chatInputState, setChatInputState] = useState({
    value: '',
    isAssetMessage: true,
  });

  const { filterGroups, resetFilterGroups } = useWatchlist(state => ({
    filterGroups: state.filterGroups,
    resetFilterGroups: state.resetFilterGroups,
  }));

  const { watchlistId, onConfirmCallback, activeListInEditMode, activeTags } =
    useWatchlistManagerMode();

  const {
    listName,
    entitiesList,
    unMappedEntitiesList,
    resolvedEntityData,
    allDuplicateTickers,
    rowSelection,
    manualEntityTags,
    countEntitiesAdded,
    watchlistError,
    watchlistErrorText,
    selectedEntitiesCount,
    savingInProgress,
    resetName,
    setSavingInProgress,
    setResolvedEntityData,
    setEntitiesList,
    setUnMappedEntitiesList,
    setAllDuplicateTickers,
    clearStore,
    updateEntitiesList,
    entityAddedClear,
    clearEntityAddedTimeout,
    validateForm,
    handleValidationError,
    onToggleAllRows,
    onRowSelectionChange,
    createNewList,
    setRowSelection,
    setWatchlistError,
  } = useWatchlistForm();

  const { hasNextPage: hasMoreEntitiesToLoad, isLoadingEntities } =
    useWatchlistFilterSync({
      filterGroups,
      containerRef,
      entitiesListLength: entitiesList.length,
      prefillTags,
      manualEntityTags,
      setEntitiesList,
      setRowSelection,
    });

  const { isPending, onChatSubmit, onSelectItem, getIsInWatchlist } =
    useEntityResolution({
      entitiesList,
      unMappedEntitiesList,
      setResolvedEntityData,
      setUnMappedEntitiesList,
      setAllDuplicateTickers,
      setWatchlistError,
      updateEntitiesList,
      entityAddedClear,
    });

  const { alertMessage, setMessage, setIsError } = useAlertMessage({
    resolvedEntityData,
    allDuplicateTickers,
  });

  useLayoutEffect(
    function initializeEditModePrefill() {
      if (!activeListInEditMode || didEditPrefillRef.current) {
        return;
      }

      const nextTags = activeListInEditMode.entities.map(e => e.tag);

      if (nextTags.length) {
        setRowSelection(buildRowSelectionFromTags(nextTags));
        entityAddedClear(nextTags.length);
      }

      setPrefillTags(nextTags);
      didEditPrefillRef.current = true;
    },
    [activeListInEditMode, setRowSelection, entityAddedClear]
  );

  const { onSave } = useWatchlistManagerSave({
    watchlistId,
    activeListInEditMode,
    activeTags,
    onConfirmCallback,
    listName,
    setSavingInProgress,
    validateForm,
    handleValidationError,
    createNewList,
    resetName,
    updateEntitiesList,
    onListCreated,
  });

  useEffect(() => setMessage(''), []);

  useEffect(() => {
    return () => {
      clearStore();
      resetFilterGroups();
      clearEntityAddedTimeout();
    };
  }, []);

  const entityChangeHandlers = useEntityChangeHandlers(setRowSelection);

  const onChatInputValueChange = (value: string, isAssetMessage = false) => {
    setChatInputState({ value, isAssetMessage });
  };

  const clearAlertMessage = () => {
    setMessage('');
    setIsError(false);
  };

  const showActionButtons =
    selectedEntitiesCount > 0 &&
    (chatInputState.isAssetMessage || !chatInputState.value);

  const isEntitiesOverflowing =
    !!containerRef.current &&
    containerRef.current?.scrollHeight > containerRef.current?.clientHeight;

  return (
    <div className={styles.container} data-testid="edit-watchlist-widget">
      <AlertMessage
        tableRef={unMappedRowRef}
        alertMessage={alertMessage}
        clearMessage={clearAlertMessage}
        isEntitiesOverflowing={isEntitiesOverflowing}
        insideModal
      />
      <div className={styles.titleWrapper}>
        <p className={styles.title}>{activeListInEditMode?.name}</p>
        <p className={styles.titleDescription}>
          {t('watchlistManager:description')}
        </p>
        <ButtonLink
          className={styles.titleStyledButtonLink}
          label={
            <a
              href={config.urls.helpImportWatchlist}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{t('watchlistManager:howToImport')}</span>
            </a>
          }
          iconName="QuestionMarkFill"
          iconSize={14}
        />
      </div>

      <div>
        <AssetFilter isPending={isPending} />
      </div>
      {entitiesList.length === 0 && unMappedEntitiesList.length === 0 && (
        <div className={styles.separator}>
          <div className={styles.separatorLine} />
          <span className={styles.separatorText}>{t('common:or')}</span>
          <div className={styles.separatorLine} />
        </div>
      )}
      <div className={styles.contentContainer}>
        <TableWatchlistManagerWidget
          data={entitiesList}
          countEntitiesAdded={countEntitiesAdded}
          toggleAllRowsSelection={onToggleAllRows}
          rowSelection={rowSelection}
          updateRowSelection={onRowSelectionChange}
          containerRef={containerRef}
          unMappedRowRef={unMappedRowRef}
          isEmptyListError={watchlistError === WatchlistError.EmptyList}
          handlers={entityChangeHandlers}
          isPending={isPending}
          hasNextPage={hasMoreEntitiesToLoad}
          isLoadingEntities={isLoadingEntities}
        />
        <p className={styles.label}>{t('watchlistManager:wantAddMore')}</p>
        <ChatWatchlistManager
          value={chatInputState.value}
          isPending={isPending}
          getIsInWatchlist={getIsInWatchlist}
          onSelectItem={onSelectItem}
          onChatSubmit={onChatSubmit}
          onChange={onChatInputValueChange}
          activeListEntityTags={activeTags}
        />
      </div>
      {showActionButtons && (
        <div className={styles.footerContainer}>
          {watchlistErrorText && (
            <>
              <Icon
                className={styles.styledIcon}
                size={20}
                iconName="ExclamationCircle"
                fillColor="var(--icon-critical)"
              />
              <p className={styles.errorMessage} data-testid="error-message">
                {watchlistErrorText}
              </p>
            </>
          )}
          <div className={styles.flexContainer}>
            <Button
              label={t('common:cancel')}
              variant="tertiary"
              size="large"
              onClick={onCancel}
            />
            <Button
              label={t('widget:watchlist.saveChanges')}
              variant="primary"
              size="large"
              onClick={onSave}
              isLoading={savingInProgress}
              disabled={savingInProgress}
              data-testid="watchlist-manager-confirm-button"
            />
          </div>
        </div>
      )}
    </div>
  );
};
